import type {ReactFrontendTool, ReactHumanInTheLoop} from "@copilotkit/react-core/v2";

import {useAgentContext, useFrontendTool, useHumanInTheLoop} from "@copilotkit/react-core/v2";
import {useAIStore} from "@project/extensions/ai-assistant";
import {useLocation} from "@tanstack/react-router";

/**
 * ContextSync connects the React/SPA state directly into the AG-UI agent's brain.
 * This ensures the agent always knows what the user is currently looking at,
 * enabling implicit context (e.g. "summarize this page").
 */
export function ContextSync() {
  const location = useLocation();

  // 1. Existing routing context
  useAgentContext({
    description: "The user's current location within the ERP application.",
    value: {
      pathname: location.pathname,
      search: location.search,
    },
  });

  // 2. Global AI Store Context
  const namespaces = useAIStore((state) => state.namespaces);

  // Group all contexts from all registered namespaces into a single object, keyed by namespace
  const aggregatedContext = (() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};

    for (const key in namespaces) {
      if (namespaces[key].context) {
        result[key] = namespaces[key].context;
      }
    }

    return result;
  })();

  useAgentContext({
    description: "The semantic state of the user's active view (tables, filters, etc.).",
    value: aggregatedContext,
  });

  // 3. Global AI Store Capabilities (Tools)
  // We flatten all tools from all namespaces
  const aggregatedTools = (() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tools: ReactFrontendTool<any>[] = [];

    for (const key in namespaces) {
      if (namespaces[key].tools) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tools.push(...(namespaces[key].tools as ReactFrontendTool<any>[]));
      }
    }

    return tools;
  })();

  // 4. Global AI Store HITL Tools
  const aggregatedHitlTools = (() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hitlTools: ReactHumanInTheLoop<any>[] = [];

    for (const key in namespaces) {
      if (namespaces[key].hitlTools) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hitlTools.push(...(namespaces[key].hitlTools as ReactHumanInTheLoop<any>[]));
      }
    }

    return hitlTools;
  })();

  return (
    <>
      {aggregatedTools.map((tool) => (
        <DynamicToolRegistrar key={tool.name} tool={tool} />
      ))}
      {aggregatedHitlTools.map((tool) => (
        <DynamicHitlToolRegistrar key={tool.name} tool={tool} />
      ))}
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DynamicToolRegistrar({tool}: {tool: ReactFrontendTool<any>}) {
  useFrontendTool(tool, [tool]);

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DynamicHitlToolRegistrar({tool}: {tool: ReactHumanInTheLoop<any>}) {
  useHumanInTheLoop(tool, [tool]);

  return null;
}
